import { useEffect, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  KNOWN_SERVICES,
  type EcosystemService,
} from '@frontend/api-clients/adminEcosystemProvisionClient';
import {
  adminRenameEcosystem,
  adminUpdateEcosystem,
  type AdminPackageAvailability,
} from '@frontend/api-clients/ecosystemsClient';
import PlatformServicesChecklist from '@frontend/components/projects/PlatformServicesChecklist';
import type { EcosystemAdminEntry } from '@frontend/types';

export interface RealmEcosystemDetailsPanelProps {
  ecosystem: EcosystemAdminEntry | null;
  packageAvailability: AdminPackageAvailability | null;
  onUpdated: (preferSelectId?: string) => void;
}

export default function RealmEcosystemDetailsPanel({
  ecosystem,
  packageAvailability,
  onUpdated,
}: RealmEcosystemDetailsPanelProps) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftId, setDraftId] = useState('');
  const [draftServices, setDraftServices] = useState<EcosystemService[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setEditing(false);
    setError('');
    setBusy(false);
    if (!ecosystem) {
      setDraftName('');
      setDraftId('');
      setDraftServices([]);
      return;
    }
    setDraftName(ecosystem.displayName || ecosystem.id);
    setDraftId(ecosystem.id);
    const known = new Set<string>(KNOWN_SERVICES);
    setDraftServices(
      (ecosystem.services || []).filter((s): s is EcosystemService => known.has(s))
    );
  }, [ecosystem]);

  if (!ecosystem) {
    return (
      <div className="admin-orch-detail">
        <p className="admin-orch-detail-empty">Select an ecosystem to view details.</p>
      </div>
    );
  }

  const beginEdit = () => {
    setDraftName(ecosystem.displayName || ecosystem.id);
    setDraftId(ecosystem.id);
    const known = new Set<string>(KNOWN_SERVICES);
    setDraftServices(
      (ecosystem.services || []).filter((s): s is EcosystemService => known.has(s))
    );
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
    setBusy(false);
    setDraftName(ecosystem.displayName || ecosystem.id);
    setDraftId(ecosystem.id);
    const known = new Set<string>(KNOWN_SERVICES);
    setDraftServices(
      (ecosystem.services || []).filter((s): s is EcosystemService => known.has(s))
    );
  };

  const confirmEdit = async () => {
    const name = draftName.trim();
    const newId = draftId.trim();
    if (!name) {
      setError('Display name is required.');
      return;
    }
    if (!newId) {
      setError('Id is required.');
      return;
    }
    if (draftServices.length === 0) {
      setError('Select at least one service.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');

      let preferSelectId = ecosystem.id;
      if (newId !== ecosystem.id) {
        await adminRenameEcosystem(token, ecosystem.id, newId);
        preferSelectId = newId;
      }

      const known = new Set<string>(KNOWN_SERVICES);
      const currentServices = (ecosystem.services || []).filter((s) => known.has(s));
      const nameChanged = name !== (ecosystem.displayName || ecosystem.id);
      const servicesChanged =
        draftServices.length !== currentServices.length ||
        draftServices.some((s) => !currentServices.includes(s));

      if (nameChanged || servicesChanged) {
        await adminUpdateEcosystem(token, preferSelectId, {
          displayName: name,
          services: draftServices,
        });
      }

      setEditing(false);
      onUpdated(preferSelectId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save ecosystem');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-orch-detail">
      <div className="admin-orch-detail-header">
        <h4>Ecosystem details</h4>
        <div className="admin-orch-detail-actions">
          {editing ? (
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void confirmEdit()}
              >
                {busy ? 'Saving…' : 'Confirm'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={beginEdit}>
              Edit
            </button>
          )}
        </div>
      </div>

      {error ? (
        <div className="settings-message settings-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="admin-orch-detail-fields">
        <label className="admin-orch-field">
          <span>Name</span>
          {editing ? (
            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              disabled={busy}
              aria-label="Ecosystem display name"
            />
          ) : (
            <p className="admin-orch-detail-readonly">
              {ecosystem.displayName || ecosystem.id}
              {ecosystem.decommissioned ? ' (decommissioned)' : ''}
            </p>
          )}
        </label>

        <label className="admin-orch-field">
          <span>Id</span>
          {editing ? (
            <input
              type="text"
              value={draftId}
              onChange={(e) => setDraftId(e.target.value)}
              disabled={busy}
              aria-label="Ecosystem id"
              pattern="[a-z][a-z0-9-]*[a-z0-9]|[a-z]"
              title="Lowercase alphanumeric with hyphens"
            />
          ) : (
            <p className="admin-orch-detail-readonly">
              <code className="admin-inline-code">{ecosystem.id}</code>
            </p>
          )}
        </label>

        <div className="admin-orch-field">
          <span>Services</span>
          {editing ? (
            <PlatformServicesChecklist
              idPrefix={`realm-ecosystem-service-${ecosystem.id}`}
              packageCatalog={packageAvailability?.catalog}
              availableServices={packageAvailability?.installedServices || []}
              realmServices={packageAvailability?.realmServices || []}
              ecosystemServices={ecosystem.services || []}
              selectedServices={draftServices}
              canAddRealmServices
              canAddEcosystemServices
              readOnly={busy}
              onChange={(services) => setDraftServices(services as EcosystemService[])}
            />
          ) : (
            <p className="admin-orch-detail-readonly">
              {(ecosystem.services || []).join(', ') || '—'}
            </p>
          )}
        </div>

        <div className="admin-orch-field">
          <span>Gitea org</span>
          <p className="admin-orch-detail-readonly">
            <code className="admin-inline-code">{ecosystem.giteaOrg || '—'}</code>
          </p>
        </div>
      </div>
    </div>
  );
}
